// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import UpcomingTrainingCard, {
    UpcomingTrainingCardStyles,
} from "@mcmc/App/Components/UpcomingTrainingCard/UpcomingTrainingCard";
import loadUpcomingTrainingAndEvents from "@mcmc/App/Handlers/UpcomingTrainingAndEvents/LoadUpcomingTrainingAndEvents";
import McMcApplicationState from "@mcmc/App/Store/McMcApplicationState";
import { getSettingsCollection } from "@mcmc/App/Store/Selectors/SettingsSelectors";
import { getUpcomingTrainingAndEvents } from "@mcmc/App/Store/Selectors/UpcomingTrainingAndEventsSelectors";
import { createSectionTitleWidget } from "@mcmc/App/Widgets/SectionTitleWidget/SectionTitleWidget";
import { styledBox } from "@mcmc/XDL/Components/Common/Box";
import { screen } from "@mcmc/XDL/Helpers";
import { layoutContainer } from "@mcmc/XDL/Utilities/sharedStyles";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: McMcApplicationState) => {
    const currentPageType = getCurrentPage(state).type;
    const {
        customSettings: { rootTrainingAndEventsCategoryUrl },
        productSettings: { catalogUrlPath },
    } = getSettingsCollection(state);
    return {
        events: getUpcomingTrainingAndEvents(state)?.eventsCollection,
        trainingAndEventsUrl: `${catalogUrlPath}${rootTrainingAndEventsCategoryUrl}`,
        isMyAccountPage: currentPageType === "MyAccountPage",
    };
};

const mapDispatchToProps = {
    loadUpcomingTrainingAndEvents,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface UpcomingTrainingAndEventsStyles {
    mainWrapper?: InjectableCss;
    innerWrapper?: InjectableCss;
    cardsContainer?: InjectableCss;
    cardStyles?: UpcomingTrainingCardStyles;
}

export const upcomingTrainingAndEventsStyles: UpcomingTrainingAndEventsStyles = {
    mainWrapper: {
        css: css`
            background-color: ${getColor("text.inverse")};
            padding: 40px 0;
        `,
    },
    innerWrapper: { css: layoutContainer },
    cardsContainer: {
        css: css`
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
            grid-gap: 30px;
            margin-top: -5px;
            ${screen("lte", "sm")`
                grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
            `}
            ${screen("lte", "xs")`
                grid-template-columns: repeat(auto-fill, minmax(255px, 1fr));
            `}
        `,
    },
};

export const myAccountTrainingStyles: UpcomingTrainingAndEventsStyles = {
    mainWrapper: {
        css: css`
            ${styledBox};
            .title-slash {
                display: none;
            }
        `,
    },
    cardsContainer: {
        css: css`
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(255px, 1fr));
            grid-gap: 20px;
            margin-top: 30px;
        `,
    },
    cardStyles: {
        wrapper: {
            css: css`
                border: none;
            `,
        },
        description: {
            size: 16,
            lineHeight: "19px",
            weight: 500,
        },
    },
};

const UpcomingTrainingAndEvents: React.FC<Props> = ({
    loadUpcomingTrainingAndEvents,
    events,
    trainingAndEventsUrl,
    isMyAccountPage,
}) => {
    React.useEffect(() => {
        loadUpcomingTrainingAndEvents({});
    }, []);

    const styles = isMyAccountPage ? myAccountTrainingStyles : upcomingTrainingAndEventsStyles;

    if (!events || events.length === 0) {
        return null;
    }

    const upcomingEvents = (
        <>
            {createSectionTitleWidget(
                translate("Upcoming Training & Events"),
                {
                    type: "Url",
                    value: trainingAndEventsUrl,
                },
                translate("View All Training"),
            )}
            <StyledWrapper {...styles.cardsContainer}>
                {events.map(event => (
                    <UpcomingTrainingCard key={event.id} {...event} extendedStyles={styles.cardStyles} />
                ))}
            </StyledWrapper>
        </>
    );

    return (
        <StyledWrapper {...styles.mainWrapper}>
            {isMyAccountPage ? (
                upcomingEvents
            ) : (
                <StyledWrapper {...styles.innerWrapper}>{upcomingEvents}</StyledWrapper>
            )}
        </StyledWrapper>
    );
};

const upcomingTrainingAndEventsWidget: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(UpcomingTrainingAndEvents),
    definition: {
        group: "Common",
    },
};

export default upcomingTrainingAndEventsWidget;
